import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  formatInfluencerPplAmount,
  getInfluencerPplMarketingShop,
  INFLUENCER_PPL_PURPOSE_CLASS,
  type InfluencerPplReport,
} from '@/utils/approval/influencerPpl';

/** 조회 화면이 이 id·class 로 버튼·그래프·펼침 줄을 바꿔 끼움 — 레거시 저장 본문과 같은 이름 */
export const INFLUENCER_PPL_DETAIL_BUTTON_ID = 'btn_detail';
export const INFLUENCER_PPL_ROW_TOGGLE_CLASS = 'btn-view-client-history';
export const INFLUENCER_PPL_HISTORY_ROW_PREFIX = 'client-history-';
export const INFLUENCER_PPL_GRAPH_PREFIX = 'pplCalGraph';

type ContentProps = {
  report: InfluencerPplReport;
  purpose: string;
  t: TFunction;
};

const InfluencerPplContent = ({ report, purpose, t }: ContentProps) => {
  const brand = report.brdNm ?? '';
  const title = t('{{brand}} 마케팅 진행의 건', {
    brand,
    interpolation: { escapeValue: false },
  });
  const tagPriceSum = formatInfluencerPplAmount(report.tagPriceSum);
  const images = report.categoryImages ?? [];
  return (
    <>
      <div className="form-group">
        <table id="influencer_report_tbl">
          <colgroup>
            <col width="75" />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <th colSpan={2} className="text-center">
                {title}
              </th>
            </tr>
            <tr>
              <th className="text-center">{t('진행업무')}</th>
              <td className="text-left">{title}</td>
            </tr>
            <tr>
              <th className="text-center">{t('진행브랜드')}</th>
              <td className="text-left">{brand}</td>
            </tr>
            <tr>
              <th className="text-center">{t('목적')}</th>
              <td className={`text-left ${INFLUENCER_PPL_PURPOSE_CLASS}`}>
                {purpose}
              </td>
            </tr>
            <tr>
              <th className="text-center">{t('예상시즌')}</th>
              <td className="text-left">{report.season ?? ''}</td>
            </tr>
            <tr>
              <th className="text-center">{t('기간')}</th>
              <td className="text-left">
                {`${report.startDt ?? ''} ~ ${report.endDt ?? ''}`}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="form-group">
        <table id="influencer_report_detail_tbl">
          <tbody>
            <tr>
              <th colSpan={5} className="text-center">
                {t('진행상세')}
              </th>
            </tr>
            <tr>
              <th className="text-center">{t('진행제품')}</th>
              <td colSpan={4} className="text-left">
                {(report.catNms ?? []).join(' / ')}
              </td>
            </tr>
            <tr>
              <th className="text-center">{t('진행도메인')}</th>
              <td colSpan={4} className="text-left">
                {(report.domainNms ?? []).join(' / ')}
              </td>
            </tr>
            <tr>
              <th className="text-center">{t('비용')}</th>
              <td colSpan={4} className="text-left">
                {t('총 : {{total}}pcs', { total: report.totalCnt ?? 0 })}
                <br />
                {`${t('TAG가')} : ${tagPriceSum}`}
                <br />
                {`${t('TAG 1/4가')} : ${formatInfluencerPplAmount(
                  report.tagPricePortion,
                )}`}
              </td>
            </tr>
            <tr>
              <th className="text-center">{t('비고')}</th>
              <td colSpan={4} className="text-left">
                {t('{{brand}} MKT 재고처리 요망 ({{shop}} : 마케팅본사)', {
                  brand,
                  shop: getInfluencerPplMarketingShop(report.brdCd),
                  interpolation: { escapeValue: false },
                })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="form-group">
        <table id="influencer_report_price_tbl">
          <tbody>
            <tr>
              <th colSpan={4} className="text-center">
                {t('진행비용')}
              </th>
            </tr>
            <tr>
              <td className="text-center title">{t('업체제안가')}</td>
              <td className="text-center title">{t('네고견적')}</td>
              <td className="text-center title">{t('네고율')}</td>
              <td rowSpan={2} className="text-center">
                <button type="button" id={INFLUENCER_PPL_DETAIL_BUTTON_ID}>
                  {t('상세보기')}
                </button>
              </td>
            </tr>
            <tr>
              <td className="text-right">{tagPriceSum}</td>
              <td className="text-right">{tagPriceSum}</td>
              <td className="text-right">0.00%</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="form-group">
        <table id="influencer_report_category_tbl">
          <colgroup>
            <col width="45" />
            <col />
            <col width="8%" />
            <col width="8%" />
            <col width="20%" />
            <col width="45" />
          </colgroup>
          <tbody>
            <tr>
              <th colSpan={6} className="text-center">
                {t('카테고리별')}
              </th>
            </tr>
            <tr>
              <td className="text-center title">NO</td>
              <td className="text-center title">{t('카테고리')}</td>
              <td className="text-center title">{t('제품수')}</td>
              <td className="text-center title">{t('수량')}</td>
              <td className="text-center title">{t('제품총액')}</td>
              <td className="title" />
            </tr>
            {(report.categoryChart ?? []).flatMap((row, idx) => [
              <tr key={`row-${idx}`}>
                <td className="text-center">{idx + 1}</td>
                <td className="text-center">{row.category ?? ''}</td>
                <td className="text-right">{row.pcs ?? ''}</td>
                <td className="text-right">{row.qty ?? ''}</td>
                <td className="text-right">
                  {formatInfluencerPplAmount(row.totalPrice)}
                </td>
                <td className="text-center">
                  <button
                    type="button"
                    className={INFLUENCER_PPL_ROW_TOGGLE_CLASS}
                    data-target={`#${INFLUENCER_PPL_HISTORY_ROW_PREFIX}${idx}`}
                  />
                </td>
              </tr>,
              <tr
                key={`history-${idx}`}
                id={`${INFLUENCER_PPL_HISTORY_ROW_PREFIX}${idx}`}
              >
                <td colSpan={6}>
                  <div className="ppl-images">
                    {images
                      .filter((image) => image.category === row.category)
                      .flatMap((image) => image.images ?? [])
                      .map((url, i) => (
                        <img key={i} src={url} alt="" />
                      ))}
                  </div>
                  <div className="ppl-graph">
                    <canvas id={`${INFLUENCER_PPL_GRAPH_PREFIX}${idx}`} />
                  </div>
                </td>
              </tr>,
            ])}
          </tbody>
        </table>
      </div>
    </>
  );
};

/** attributes.content — 작성자 언어로 저장. 조회 화면은 이 HTML 위에 버튼·그래프를 다시 그림 */
export const buildInfluencerPplContentHtml = (props: ContentProps): string =>
  renderToStaticMarkup(<InfluencerPplContent {...props} />);
